import { InfiniteData } from '@tanstack/react-query';
import camelcaseKeys from 'camelcase-keys';

import { UnifiedProduct } from '../types/product';

export interface NormalizedCioResponseInfiniteData<T> extends InfiniteData<T> {
  products?: UnifiedProduct[];
}

/**
 * @param response - Infinite query response from useCioBrowseProducts() or useCioSearchProducts()
 * @returns {Object} - Normalized response with flattened products array
 */
export const normalizeCioResponse = (
  response: InfiniteData<any>
): NormalizedCioResponseInfiniteData<any> => {
  return {
    ...response,
    products: response.pages
      .flatMap((page: any) => {
        if (!page.response?.results) {
          return [];
        }

        return page.response?.results?.flatMap((result: any) => {
          if (!result?.data) {
            return null;
          }

          // Cio response keys are snake_cased by default, convert to camelCase
          const camelCasedResult = camelcaseKeys(result?.data, {
            deep: true,
          });

          return {
            ...camelCasedResult,
            id: Number(camelCasedResult.id),
            masterProductId: Number(camelCasedResult.id),
            tagIdList:
              camelCasedResult?.tagIdList?.map((tag: any) => Number(tag)) || [],
            featuredProductLocationIdList:
              camelCasedResult?.featuredProductLocationIdList?.map(
                (location: any) => Number(location)
              ) || [],
            productCategoryIdList:
              camelCasedResult?.productCategoryIdList?.map((category: any) =>
                Number(category)
              ) || [],
            activeFeaturedProductLocationCodeList:
              camelCasedResult?.activeFeaturedProductLocationCodeList?.map(
                (location: any) => String(location?.featuredProductLocationCode)
              ) || [],
            activeFeaturedProductLocationIdList:
              camelCasedResult?.activeFeaturedProductLocationIdList?.map(
                (location: any) => Number(location?.featuredProductLocationId)
              ) || [],
          };
        });
      })
      .filter(Boolean),
  };
};
